import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Anchor, Burger, Button, Drawer, Group, Stack, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Logo from '@/components/Logo';
import { useCloseOnDesktop } from '@/hooks/useCloseOnDesktop';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { scrollBehavior } from '@/lib/motion';
import { ROUTES } from '@/routes/paths';
import { useAuth } from '@/stores/authContext';
import classes from '@/components/navLink.module.css';
import touchTarget from '@/components/common/touchTarget.module.css';

interface SectionAnchor {
  readonly id: string;
  readonly key: string;
}

// Якоря секций лендинга (id совпадают с id секций: how/trade/certification/forwarding).
const ANCHORS: readonly SectionAnchor[] = [
  { id: 'how', key: 'nav.anchors.how' },
  { id: 'trade', key: 'nav.anchors.trade' },
  { id: 'certification', key: 'nav.anchors.certification' },
  { id: 'forwarding', key: 'nav.anchors.forwarding' },
];

// Стабильная ссылка для зависимости эффекта scroll-spy (иначе новый массив каждый рендер).
const ANCHOR_IDS = ANCHORS.map((anchor) => anchor.id);

export default function PublicHeader() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [opened, { toggle, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const location = useLocation();
  // На главной активный пункт меню следует за прокруткой (scroll-spy); на других страницах — выкл.
  useCloseOnDesktop(close);
  const activeSection = useScrollSpy(ANCHOR_IDS, location.pathname === ROUTES.HOME);

  function scrollToSection(id: string): void {
    close();
    if (location.pathname === ROUTES.HOME) {
      document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    } else {
      navigate(ROUTES.HOME);
      // дождаться рендера лендинга, затем проскроллить к якорю
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      }, 120);
    }
  }

  // Заголовок бургер-меню «Дата 1861» работает как клик по лого: на главной — скролл наверх,
  // иначе переход на главную и наверх. Дополнительно закрывает Drawer.
  function goHome(): void {
    close();
    if (location.pathname === ROUTES.HOME) {
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
    } else {
      navigate(ROUTES.HOME);
      window.setTimeout(() => window.scrollTo({ top: 0 }), 0);
    }
  }

  const anchorLinks = ANCHORS.map(({ id, key }) => (
    <Anchor
      key={id}
      component="button"
      type="button"
      onClick={() => scrollToSection(id)}
      className={`${classes.link}${activeSection === id ? ` ${classes.linkActive}` : ''}`}
      underline="never"
    >
      {t(key)}
    </Anchor>
  ));

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap">
      <Group gap="lg" wrap="nowrap">
        <Logo offsetY={-3} />
        <Group component="nav" aria-label={t('nav.mainLabel')} gap="md" visibleFrom="md">
          {anchorLinks}
        </Group>
      </Group>

      <Group gap="md" visibleFrom="md" wrap="nowrap">
        <Anchor
          component={Link}
          to={ROUTES.ABOUT}
          className={`${classes.link}${location.pathname === ROUTES.ABOUT ? ` ${classes.linkActive}` : ''}`}
          underline="never"
        >
          {t('nav.about')}
        </Anchor>
        {isAuthenticated ? (
          <Button component={Link} to={ROUTES.APP} size="sm">
            {t('notFound.toCabinet')}
          </Button>
        ) : (
          <>
            <Button component={Link} to={ROUTES.LOGIN} variant="default" size="sm">
              {t('nav.login')}
            </Button>
            <Button component={Link} to={ROUTES.REGISTER} size="sm">
              {t('nav.register')}
            </Button>
          </>
        )}
      </Group>

      <Group gap="xs" hiddenFrom="md" wrap="nowrap">
        <Button component={Link} to={isAuthenticated ? ROUTES.APP : ROUTES.REGISTER} size="compact-sm">
          {t(isAuthenticated ? 'notFound.toCabinet' : 'nav.register')}
        </Button>
        <Burger
          opened={opened}
          onClick={toggle}
          size="sm"
          className={touchTarget.target}
          aria-label={t('nav.menu')}
          aria-expanded={opened}
        />
      </Group>

      <Drawer
        opened={opened}
        onClose={close}
        position="right"
        size="xs"
        hiddenFrom="md"
        title={
          <UnstyledButton onClick={goHome} fw={700} fz="md" c="var(--mantine-color-text)">
            {t('app.name')}
          </UnstyledButton>
        }
        styles={{
          header: { position: 'relative', justifyContent: 'center' },
          title: { marginRight: 0 },
          close: { position: 'absolute', right: 'var(--mantine-spacing-md)', top: '50%', transform: 'translateY(-50%)' },
        }}
      >
        <Stack gap="sm">
          {ANCHORS.map(({ id, key }) => (
            <Anchor
              key={id}
              component="button"
              type="button"
              onClick={() => scrollToSection(id)}
              className={`${classes.link}${activeSection === id ? ` ${classes.linkActive}` : ''}`}
              underline="never"
              ta="center"
            >
              {t(key)}
            </Anchor>
          ))}
          <Anchor
            component={Link}
            to={ROUTES.ABOUT}
            onClick={close}
            className={`${classes.link}${location.pathname === ROUTES.ABOUT ? ` ${classes.linkActive}` : ''}`}
            underline="never"
            ta="center"
          >
            {t('nav.about')}
          </Anchor>
          {isAuthenticated ? (
            <Button component={Link} to={ROUTES.APP} onClick={close} fullWidth size="sm">
              {t('notFound.toCabinet')}
            </Button>
          ) : (
            <>
              <Button component={Link} to={ROUTES.LOGIN} variant="default" onClick={close} fullWidth size="sm">
                {t('nav.login')}
              </Button>
              <Button component={Link} to={ROUTES.REGISTER} onClick={close} fullWidth size="sm">
                {t('nav.register')}
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    </Group>
  );
}
